import { useCallback, useRef } from "react";
import { useSessionState } from "./useSessionState";

interface ResizableListPanelOptions {
  /** Unique sessionStorage key so each list/detail split remembers its own width. */
  storageKey: string;
  defaultWidth: number;
  minWidth: number;
  maxWidth: number;
}

/**
 * Drag-to-resize the list column of a list/detail split layout, persisted across
 * refreshes and tab switches. Used by the email, clients and contacts pages.
 *
 * The drag is driven straight through the DOM rather than through React state:
 * mousemove writes `panelRef.current.style.width` directly and React state is
 * committed once on mouseup. Going through state instead would re-render the whole
 * page tree ~100x/sec — on Clients/Contacts that means re-serialising every `sx`
 * prop and re-rendering an unvirtualised list of every client/contact on each frame,
 * plus a synchronous sessionStorage write per frame from useSessionState. That is
 * what made those two pages lag; the email pages only got away with it because
 * EmailPage itself is cheap to re-render.
 */
export const useResizableListPanel = ({
  storageKey,
  defaultWidth,
  minWidth,
  maxWidth,
}: ResizableListPanelOptions) => {
  const [width, setWidth] = useSessionState<number>(storageKey, defaultWidth);
  /** Attach to the list column element so the drag can size it without re-rendering. */
  const panelRef = useRef<HTMLDivElement | null>(null);
  // Mirrors `width` so handleResizeStart doesn't need it as a dependency — keeping the
  // callback identity stable, which in turn keeps ResizeHandle's props stable.
  const widthRef = useRef(width);
  widthRef.current = width;

  const handleResizeStart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      const startX = e.clientX;
      const startWidth = widthRef.current;
      let latestWidth = startWidth;
      let frame = 0;
      // The pointer leaves the thin handle almost immediately once dragging starts,
      // so keep the resize cursor and kill text selection for the whole drag.
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";

      const applyWidth = () => {
        frame = 0;
        if (panelRef.current) {
          panelRef.current.style.width = `${latestWidth}px`;
        }
      };

      const handleMouseMove = (moveEvent: MouseEvent) => {
        latestWidth = Math.min(
          maxWidth,
          Math.max(minWidth, startWidth + (moveEvent.clientX - startX)),
        );
        // Coalesce bursts of mousemove into one style write per animation frame.
        if (!frame) frame = requestAnimationFrame(applyWidth);
      };

      const handleMouseUp = () => {
        if (frame) cancelAnimationFrame(frame);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        // Single render + single sessionStorage write for the whole drag. The inline
        // style set above and this state now agree, so the commit is visually a no-op.
        setWidth(latestWidth);
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    },
    [setWidth, minWidth, maxWidth],
  );

  return { width, panelRef, handleResizeStart };
};
